import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PhilosophySection = () => {
  const philosophyPillars = [
    {
      icon: "Heart",
      title: "Paixão pela Madeira",
      description: "Cada peça de madeira tem sua própria história e personalidade. Nosso trabalho é revelar sua beleza natural e transformá-la em móveis únicos que emocionam.",
      color: "text-red-600"
    },
    {
      icon: "Users",
      title: "Artesanato Colaborativo",
      description: "Acreditamos que os melhores móveis nascem da colaboração entre artesãos experientes, cada um contribuindo com sua especialidade única para o resultado final.",
      color: "text-blue-600"
    },
    {
      icon: "TreePine",
      title: "Sustentabilidade",
      description: "Utilizamos apenas madeiras certificadas e práticas sustentáveis, garantindo que nossa arte preserve o meio ambiente para as futuras gerações.",
      color: "text-green-600"
    },
    {
      icon: "Sparkles",
      title: "Personalização Total",
      description: "Cada cliente é único, assim como cada móvel que criamos. Trabalhamos em estreita colaboração para materializar visões e sonhos em peças funcionais.",
      color: "text-yellow-600"
    }
  ];

  const values = [
    {
      title: "Excelência Artesanal",
      description: "Compromisso inabalável com a qualidade superior em cada detalhe",
      icon: "Award"
    },
    {
      title: "Inovação Respeitosa",
      description: "Equilibramos tradição centenária com técnicas modernas",
      icon: "Lightbulb"
    },
    {
      title: "Relacionamento Duradouro",
      description: "Construímos parcerias que vão além da entrega do móvel",
      icon: "Handshake"
    },
    {
      title: "Transparência Total",
      description: "Processo aberto desde a seleção da madeira até a entrega",
      icon: "Eye"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-primary/5 via-white to-secondary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Compass" size={16} />
            <span>Nossa Filosofia</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Tradição Artesanal,
            <span className="block text-secondary">Design Contemporâneo</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Nossa filosofia une o melhor da tradição marceneira brasileira com inovação e design contemporâneo, 
            criando móveis que são verdadeiras obras de arte funcionais.
          </p>
        </div>

        {/* Philosophy Pillars */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {philosophyPillars?.map((pillar, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-premium border border-border hover:shadow-premium-lg transition-all duration-300 group"
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <Icon name={pillar?.icon} size={32} color="var(--color-primary)" strokeWidth={1.5} />
              </div>
              
              <h3 className="font-playfair text-xl font-bold text-primary mb-4">
                {pillar?.title}
              </h3>
              
              <p className="text-text-secondary leading-relaxed">
                {pillar?.description}
              </p>
            </div>
          ))}
        </div>

        {/* Main Philosophy Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Content */}
          <div className="space-y-8">
            <div>
              <h3 className="font-playfair text-3xl font-bold text-primary mb-6">
                Móveis que Contam Histórias
              </h3>
              
              <div className="space-y-6 text-text-secondary leading-relaxed">
                <p>
                  Na PH Móveis, acreditamos que cada móvel deve ser mais do que apenas funcional – 
                  deve ser uma extensão da personalidade de quem o possui. Cada peça que criamos 
                  carrega em si a história da madeira, a paixão do artesão e os sonhos do cliente.
                </p>
                
                <p>
                  Nossa abordagem artesanal celebra o toque humano em um mundo cada vez mais automatizado. 
                  Cada entalhe, cada acabamento, cada detalhe é cuidadosamente trabalhado à mão, 
                  garantindo que não existam duas peças exatamente iguais.
                </p>
                
                <p>
                  Trabalhamos em estreita colaboração com nossos clientes, desde a concepção inicial 
                  até a entrega final, garantindo que cada móvel reflita perfeitamente sua visão e 
                  necessidades específicas.
                </p>
              </div>
            </div>

            {/* Values Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {values?.map((value, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-gradient-forest rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={value?.icon} size={20} color="white" strokeWidth={2} />
                  </div>
                  <div>
                    <h4 className="font-medium text-primary mb-1">{value?.title}</h4>
                    <p className="text-sm text-text-secondary">{value?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="relative overflow-hidden rounded-2xl shadow-premium">
                  <Image
                    src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    alt="Artesão trabalhando com madeira"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="relative overflow-hidden rounded-2xl shadow-premium">
                  <Image
                    src="https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    alt="Detalhes de acabamento em madeira"
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              
              <div className="space-y-6 pt-12">
                <div className="relative overflow-hidden rounded-2xl shadow-premium">
                  <Image
                    src="https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    alt="Móvel artesanal finalizado"
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="relative overflow-hidden rounded-2xl shadow-premium">
                  <Image
                    src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                    alt="Ferramentas de marcenaria"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>

            {/* Floating Quote */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-premium p-6 border border-border max-w-xs">
              <Icon name="Quote" size={24} color="var(--color-accent)" className="mb-3" />
              <p className="text-sm text-text-primary italic">
                "Cada peça é única, feita especialmente para você"
              </p>
              <p className="text-xs text-text-secondary mt-2">- Paulo Henrique, Fundador</p>
            </div>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="bg-gradient-forest rounded-3xl p-12 text-center text-white">
          <Icon name="Target" size={48} color="white" className="mx-auto mb-6" />
          <h3 className="font-playfair text-3xl font-bold mb-6">Nossa Missão</h3>
          <p className="text-lg leading-relaxed max-w-4xl mx-auto opacity-90">
            Criar móveis artesanais únicos que transformem casas em lares, combinando tradição centenária 
            com design contemporâneo, sempre respeitando o meio ambiente e valorizando o trabalho humano. 
            Nosso compromisso é com a excelência, a sustentabilidade e a satisfação completa de nossos clientes.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;